import { useEffect, useRef } from 'react';

import { type IYmapsKeys, type IYmapsMap, loadYmapsApi, type TYmapsCoordinates } from 'shared/utils/ymaps';

import placemarkIconUrl from './mapHomeSpot.png';
import style from './style.css';

/** Как `object_on_map.tsx` монолита: без зума колесом мыши, чтобы карта не перехватывала прокрутку страницы */
const MAP_MODULES = [
  'Map',
  'Placemark',
  'behavior.Drag',
  'behavior.DblClickZoom',
  'behavior.RightMouseButtonMagnifier',
  'behavior.MultiTouch',
  'control.ZoomControl',
];
const MAP_BEHAVIORS = ['drag', 'dblClickZoom', 'rightMouseButtonMagnifier', 'multiTouch'];

export interface IObjectOnMapProps {
  coordinates: TYmapsCoordinates;
  zoom: number;
  keys: IYmapsKeys;
  onError(error: unknown): void;
}

/** Карта с меткой здания */
export const ObjectOnMap = ({ coordinates, zoom, keys, onError }: IObjectOnMapProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [lat, lng] = coordinates;
  const { apiKey, suggestApiKey } = keys;

  useEffect(() => {
    let map: IYmapsMap | null = null;
    let isCancelled = false;

    loadYmapsApi({ apiKey, suggestApiKey }, MAP_MODULES)
      .then(ymaps => {
        if (isCancelled || !ref.current) {
          return;
        }

        map = new ymaps.Map(ref.current, {
          center: [lat, lng],
          zoom,
          behaviors: MAP_BEHAVIORS,
          controls: [new ymaps.control.ZoomControl()],
        });
        map.geoObjects.add(
          new ymaps.Placemark(
            [lat, lng],
            {},
            {
              iconLayout: 'default#image',
              iconImageHref: placemarkIconUrl,
              iconImageSize: [52, 48],
              iconImageOffset: [-20, -54],
            },
          ),
        );
      })
      .catch(onError);

    return () => {
      isCancelled = true;
      map?.destroy();
    };
  }, [lat, lng, zoom, apiKey, suggestApiKey, onError]);

  return <div ref={ref} className={style['map']} data-name="ObjectOnMap" />;
};
